import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import EncyroLink from "../components/EncyroLink";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll be in touch within one business day.");
      setDone(true);
      setForm({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="bg-[#F9F8F6]">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-12">
        <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-6">— Contact</div>
        <h1 className="font-serif text-5xl md:text-6xl text-[#0F292F] font-light tracking-tight max-w-3xl leading-[1.05]">
          Begin the <span className="italic text-[#8E6A2B]">conversation</span>.
        </h1>
        <p className="mt-8 text-lg text-[#52525B] max-w-2xl leading-relaxed">
          Tell us a little about your situation. We'll respond within one business day to set up a 30-minute discovery call.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5 space-y-10">
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-[0.2em] text-[#8E6A2B]">Rockville Office</div>
            <div className="flex items-start gap-4 text-[#1A1A1A]">
              <MapPin size={18} className="text-[#0F292F] mt-1" />
              <span>451 N. Hungerford Dr., Suite 119<br />Rockville, MD 20850</span>
            </div>
            <div className="flex items-center gap-4 text-[#1A1A1A]">
              <Phone size={18} className="text-[#0F292F]" />
              <a href="tel:+15715985790" className="he-link">(571) 598-5790</a>
            </div>
            <div className="flex items-center gap-4 text-[#1A1A1A]">
              <Mail size={18} className="text-[#0F292F]" />
              <a href="mailto:helliotassociates@gmail.com" className="he-link">helliotassociates@gmail.com</a>
            </div>
          </div>

          <div className="border-l-2 border-[#8E6A2B] pl-6 py-2">
            <div className="text-xs uppercase tracking-[0.2em] text-[#8E6A2B] mb-2">Hours</div>
            <p className="text-[#1A1A1A]">Mon – Fri · 8:30am – 5:30pm ET<br />Saturday by appointment</p>
          </div>

          <div className="bg-white border border-black/10 p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-[#8E6A2B] mb-3">Privacy & Compliance</div>
            <p className="text-sm text-[#52525B] leading-relaxed">
              Your inquiry is confidential. Submissions are stored on encrypted servers
              accessible only to firm advisors. See our{" "}
              <a href="/privacy" className="text-[#0F292F] underline">Privacy Policy</a>.
            </p>
          </div>

          <EncyroLink variant="card" />
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 bg-white border border-black/10 p-10 md:p-14 space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <label className="block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Full Name *</div>
              <input
                data-testid="contact-name"
                required
                className="he-input"
                value={form.name}
                onChange={onChange("name")}
                placeholder="Jane Doe"
              />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Email *</div>
              <input
                type="email"
                data-testid="contact-email"
                required
                className="he-input"
                value={form.email}
                onChange={onChange("email")}
                placeholder="jane@example.com"
              />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Phone</div>
              <input
                data-testid="contact-phone"
                className="he-input"
                value={form.phone}
                onChange={onChange("phone")}
                placeholder="(571) 555-0000"
              />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Subject</div>
              <select
                data-testid="contact-subject"
                className="he-input bg-transparent"
                value={form.subject}
                onChange={onChange("subject")}
              >
                <option>General Inquiry</option>
                <option>Retirement Planning</option>
                <option>Individual Tax Preparation</option>
                <option>Small-Business Tax Planning</option>
                <option>IRS Notice / Representation</option>
                <option>Other</option>
              </select>
            </label>
          </div>
          <label className="block">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Message *</div>
            <textarea
              data-testid="contact-message"
              required
              rows={5}
              className="he-input resize-none"
              value={form.message}
              onChange={onChange("message")}
              placeholder="Tell us a little about your situation and what you're hoping to address..."
            />
          </label>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <p className="text-xs text-[#52525B] max-w-xs">
              By submitting, you consent to our processing your information per our Privacy Policy.
            </p>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={submitting}
              className="btn-primary disabled:opacity-60"
            >
              {submitting ? "Sending..." : (<>Send Message <ArrowRight size={16} /></>)}
            </button>
          </div>
          {done && (
            <div data-testid="contact-success" className="text-sm text-[#0F292F] border-t border-[#8E6A2B] pt-6">
              ✓ Thank you. We'll be in touch within one business day.
            </div>
          )}
        </form>
      </section>
    </div>
  );
}
